import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, X } from 'lucide-react';
import { Channel } from '../types/channel';
import { apiClient, ChannelSubscriber } from '../lib/apiClient';
import BodyPortal from './BodyPortal';

interface ChannelSubscribersModalProps {
  channel: Channel;
  onClose: () => void;
}

/** Owner-only list of a channel's subscribers (name when known, email, when they subscribed). */
const ChannelSubscribersModal: React.FC<ChannelSubscribersModalProps> = ({ channel, onClose }) => {
  const { t, i18n } = useTranslation();
  const [subscribers, setSubscribers] = useState<ChannelSubscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient.listChannelSubscribers(channel.id, channel.slug).then(({ data, error }) => {
      if (cancelled) return;
      if (error) setFailed(true);
      else setSubscribers(data ?? []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [channel.id, channel.slug]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const secondary = 'var(--text-secondary, #6b7280)';

  return (
    <BodyPortal>
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('channel.subscribers_title', 'Subscribers')}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg, var(--card, #fff))',
          color: 'var(--card-foreground, var(--text-primary, #111))',
          borderRadius: 12, width: 420, maxWidth: '100%', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '16px 20px',
          borderBottom: '1px solid var(--theme-border, #e5e7eb)',
        }}>
          <Users size={18} aria-hidden />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, flex: 1 }}>
            {t('channel.subscribers_title', 'Subscribers')}
            {!loading && !failed && (
              <span style={{ fontWeight: 400, color: secondary }}> · {subscribers.length}</span>
            )}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close', 'Close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondary, padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '8px 0' }}>
          {loading && (
            <p style={{ margin: 0, padding: '16px 20px', fontSize: 13, color: secondary }}>
              {t('channel.subscribers_loading', 'Loading subscribers…')}
            </p>
          )}
          {!loading && failed && (
            <p style={{ margin: 0, padding: '16px 20px', fontSize: 13, color: 'var(--theme-error, #dc2626)' }}>
              {t('channel.subscribers_error', 'Could not load subscribers.')}
            </p>
          )}
          {!loading && !failed && subscribers.length === 0 && (
            <p style={{ margin: 0, padding: '16px 20px', fontSize: 13, color: secondary }}>
              {t('channel.subscribers_empty', 'No subscribers yet.')}
            </p>
          )}
          {!loading && !failed && subscribers.map((s) => (
            <div
              key={s.subscriberEmail}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px' }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'var(--theme-primary, #1976d2)', color: 'var(--button-primary-foreground, #fff)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600,
              }}>
                {(s.subscriberName || s.subscriberEmail).charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.subscriberName || s.subscriberEmail}
                </div>
                {s.subscriberName && (
                  <div style={{ fontSize: 12, color: secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.subscriberEmail}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 12, color: secondary, flexShrink: 0 }}>
                {new Date(s.subscribedAt).toLocaleDateString(i18n.language)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
    </BodyPortal>
  );
};

export default ChannelSubscribersModal;
